/**
 * Allauth headless API client library.
 * Wraps all allauth browser/v1 endpoints using the project's axios instance.
 */

import * as endPoints from '@/endpoints'
import { apiOrigin } from '@/lib/apiOrigin'
import axios from '@/lib/axios'
import { AuthProcess } from './flows'

// ── Base URLs ──────────────────────────────────────────────────────────
const _apiRoot = apiOrigin()

const BASE = `${_apiRoot}/api/auth/browser/v1`
const AUTH = `${BASE}/auth`
const ACCOUNT = `${BASE}/account`

// ── Custom event for cross-component auth state sync ───────────────────
const AUTH_CHANGE_EVENT = 'allauth.auth.change'

function dispatchAuthChange(detail) {
    document.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT, { detail }))
}

export function onAuthChange(callback) {
    const handler = (e) => callback(e.detail)
    document.addEventListener(AUTH_CHANGE_EVENT, handler)
    return () => document.removeEventListener(AUTH_CHANGE_EVENT, handler)
}

// ── Flow constants and pending-flow helpers (dependency-free, see flows.js) ──
export {
    AuthenticatorType,
    AuthProcess,
    Flows,
    followPendingFlow,
    getPendingFlow,
    mfaAlternatives,
    needsReauthentication,
    pendingFlowUrl,
    pendingMfaTypes
} from './flows'

// ── Helpers ────────────────────────────────────────────────────────────

/**
 * Build absolute URL from relative path (for OAuth redirects).
 */
function absoluteUrl(path) {
    const { protocol, hostname, port } = window.location
    const portPart = port ? `:${port}` : ''
    return `${protocol}//${hostname}${portPart}${path}`
}

/**
 * POST a form (non-XHR) — used for social provider redirects.
 * Creates a hidden form and submits it, navigating the browser.
 */
export function postForm(action, data) {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = action

    for (const [key, value] of Object.entries(data)) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
}

// ── Unwrap helper: returns the response data, dispatches auth change ──
async function request(method, url, data = undefined) {
    try {
        const resp = await axios({ method, url, data })
        const result = resp.data
        // Dispatch auth change for session-affecting calls
        if (
            result?.data?.user !== undefined ||
            result?.meta?.is_authenticated !== undefined
        ) {
            dispatchAuthChange(result)
        }
        return result
    } catch (err) {
        // allauth headless uses 401 as a meaningful status (e.g. pending 2FA, pending email
        // verification, code-based password reset). Axios throws on non-2xx, so we intercept
        // 401 responses that carry a valid allauth body and return them normally.
        if (err.response?.status === 401) {
            const result = err.response.data
            if (result?.meta?.is_authenticated !== undefined) {
                dispatchAuthChange(result)
                return result
            }
        }
        throw err
    }
}

// ── Configuration ──────────────────────────────────────────────────────

export function getConfig() {
    return request('GET', `${BASE}/config`)
}

// ── Session ────────────────────────────────────────────────────────────

export function getSession() {
    return request('GET', `${AUTH}/session`)
}

// ── Authentication ─────────────────────────────────────────────────────

export function login(data) {
    return request('POST', `${AUTH}/login`, data)
}

export function signup(data) {
    return request('POST', `${AUTH}/signup`, data)
}

export function logout() {
    return request('DELETE', `${AUTH}/session`)
}

// ── Email verification ─────────────────────────────────────────────────

export function getEmailVerification() {
    return request('GET', `${AUTH}/email/verify`)
}

export function verifyEmail(data) {
    return request('POST', `${AUTH}/email/verify`, data)
}

export function resendVerificationEmail(data) {
    return request('POST', `${AUTH}/email/verify/resend`, data)
}

// ── Password ───────────────────────────────────────────────────────────

export function requestPasswordReset(data) {
    return request('POST', `${AUTH}/password/request`, data)
}

export function resetPassword(data) {
    return request('POST', `${AUTH}/password/reset`, data)
}

export function changePassword(data) {
    return request('POST', `${ACCOUNT}/password/change`, data)
}

// ── Account email management ───────────────────────────────────────────

export function getEmailAddresses() {
    return request('GET', `${ACCOUNT}/email`)
}

export function addEmail(data) {
    return request('POST', `${ACCOUNT}/email`, data)
}

export function deleteEmail(data) {
    return request('DELETE', `${ACCOUNT}/email`, data)
}

export function markEmailAsPrimary(data) {
    return request('PATCH', `${ACCOUNT}/email`, data)
}

// ── Social / Provider accounts ─────────────────────────────────────────

export function getProviderAccounts() {
    return request('GET', `${ACCOUNT}/providers`)
}

export function disconnectProvider(data) {
    return request('DELETE', `${ACCOUNT}/providers`, data)
}

export function redirectToProvider(
    provider,
    callbackUrl,
    process = AuthProcess.LOGIN
) {
    postForm(`${AUTH}/provider/redirect`, {
        provider,
        callback_url: callbackUrl || absoluteUrl('/account/provider/callback'),
        process
    })
}

export function providerToken(data) {
    return request('POST', `${AUTH}/provider/token`, data)
}

export function providerSignup(data) {
    return request('POST', `${AUTH}/provider/signup`, data)
}

export function discoverTenantLogin(data) {
    return request('POST', endPoints.AUTH_TENANT_DISCOVER, data)
}

export async function redirectToTenantProvider(discoveryToken, callbackUrl) {
    const result = await request('POST', endPoints.AUTH_TENANT_REDIRECT, {
        discovery_token: discoveryToken,
        callback_url: callbackUrl || absoluteUrl('/account/provider/callback')
    })
    if (result?.redirect_url) {
        window.location.assign(result.redirect_url)
    }
    return result
}

// ── MFA: authenticators ────────────────────────────────────────────────

export function getAuthenticators() {
    return request('GET', `${ACCOUNT}/authenticators`)
}

// ── MFA: TOTP ──────────────────────────────────────────────────────────

export function getTOTPAuthenticator() {
    return request('GET', `${ACCOUNT}/authenticators/totp`)
}

export function activateTOTP(data) {
    return request('POST', `${ACCOUNT}/authenticators/totp`, data)
}

export function deactivateTOTP() {
    return request('DELETE', `${ACCOUNT}/authenticators/totp`)
}

// ── MFA: Recovery codes ────────────────────────────────────────────────

export function getRecoveryCodes() {
    return request('GET', `${ACCOUNT}/authenticators/recovery-codes`)
}

export function generateRecoveryCodes() {
    return request('POST', `${ACCOUNT}/authenticators/recovery-codes`)
}

// ── MFA: WebAuthn (security keys and passkeys) ─────────────────────────
// The browser half (navigator.credentials) lives in ./webauthn.js; these only
// talk to allauth. Shapes follow allauth headless (django-allauth 65.x).

/** Creation options for a new key; `passwordless` asks for a passkey. */
export function getWebAuthnCreateOptions({ passwordless = false } = {}) {
    const query = passwordless ? '?passwordless' : ''
    return request('GET', `${ACCOUNT}/authenticators/webauthn${query}`)
}

/** Save a new key: `credential` is the browser's RegistrationResponseJSON. */
export function addWebAuthnCredential({ name, credential }) {
    return request('POST', `${ACCOUNT}/authenticators/webauthn`, {
        name,
        credential
    })
}

export function renameWebAuthnCredential(id, name) {
    return request('PUT', `${ACCOUNT}/authenticators/webauthn`, { id, name })
}

export function deleteWebAuthnCredentials(ids) {
    return request('DELETE', `${ACCOUNT}/authenticators/webauthn`, {
        authenticators: ids
    })
}

// The second step of a password sign-in, with a key.
export function getWebAuthnAuthenticateOptions() {
    return request('GET', `${AUTH}/webauthn/authenticate`)
}

export function authenticateWebAuthn(credential) {
    return request('POST', `${AUTH}/webauthn/authenticate`, { credential })
}

// Signing in with a passkey alone (MFA_PASSKEY_LOGIN_ENABLED).
export function getWebAuthnLoginOptions() {
    return request('GET', `${AUTH}/webauthn/login`)
}

export function loginWithWebAuthn(credential) {
    return request('POST', `${AUTH}/webauthn/login`, { credential })
}

// Confirming who you are with a key before a sensitive change.
export function getWebAuthnReauthenticateOptions() {
    return request('GET', `${AUTH}/webauthn/reauthenticate`)
}

export function reauthenticateWebAuthn(credential) {
    return request('POST', `${AUTH}/webauthn/reauthenticate`, { credential })
}

// ── MFA: Authenticate / Reauthenticate ─────────────────────────────────

export function mfaAuthenticate(data) {
    return request('POST', `${AUTH}/2fa/authenticate`, data)
}

export function mfaReauthenticate(data) {
    return request('POST', `${AUTH}/2fa/reauthenticate`, data)
}

export function mfaTrust(data) {
    return request('POST', `${AUTH}/2fa/trust`, data)
}

// ── Sessions ──────────────────────────────────────────────────────────

export function getSessions() {
    return request('GET', `${AUTH}/sessions`)
}

export function deleteSession(data) {
    return request('DELETE', `${AUTH}/sessions`, data)
}

// ── Reauthenticate (password) ──────────────────────────────────────────

export function reauthenticate(data) {
    return request('POST', `${AUTH}/reauthenticate`, data)
}
